import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronLeft, ChevronRight, ChevronDown, Save, Trash2, History, AlertCircle,
  Wand2, MessageCircle, GitCompare, Divide, Info, ArrowRightLeft, FileText, FileDown, Printer,
} from 'lucide-react';
import { getProperties } from '@/lib/api';
import { getBankTransactions, getTransactionCategories } from '@/lib/bankTransactionsApi';
import {
  getFlexibleReport, getFlexibleAggregate, upsertFlexibleAdjustment, clearFlexibleAdjustment,
  upsertFlexibleRule, getFlexibleVersions, createFlexibleVersion, deleteFlexibleVersion,
  aiClassifyFlexible, aiParseFlexibleInstruction, downloadFlexibleExport,
} from '@/lib/flexibleReportsApi';
import TransactionEditDialog from '@/components/TransactionEditDialog';

/* ─── Helpers ─── */
const fmt = (v) => {
  const n = Number(v || 0);
  const neg = n < 0;
  const s = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Math.abs(n));
  return neg ? `-${s}` : s;
};
const fmtDateShort = (d) => {
  if (!d || d.length < 10) return d || '';
  const [y, m, day] = d.split('-').map(Number);
  return `${m}/${day}/${y}`;
};
const addMonths = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const SECTION_META = {
  cog:          { label: 'Cost of Goods',    abbr: 'COG' },
  variable:     { label: 'Variable',         abbr: 'Var' },
  general:      { label: 'General Overhead', abbr: 'Gen' },
  unclassified: { label: 'Unclassified',     abbr: '?' },
};

/* ─── Transaction row (matches ReportsPage aesthetic) ─── */
function TxnRow({ tx, onOpen, onClassify, onToggleAmortize, onMoveMonth, isReadOnly, currentMonth: currentMonthYm }) {
  const display = tx.title || tx.description || '(no description)';
  const [popOpen, setPopOpen] = useState(false);
  const [moveMode, setMoveMode] = useState(false);
  const [moveTarget, setMoveTarget] = useState(currentMonthYm);

  useEffect(() => { if (!popOpen) { setMoveMode(false); setMoveTarget(currentMonthYm); } }, [popOpen, currentMonthYm]);

  return (
    <div className="group w-full flex items-center gap-1 px-1 py-0.5 rounded-sm hover:bg-[#f5f0eb]/60 transition-colors text-[11px] text-[#5b6773]" data-testid="flex-tx-row">
      <button
        onClick={() => onOpen(tx)}
        className="flex-1 min-w-0 flex items-center gap-1 text-left"
        data-testid="flex-tx-open"
      >
        <span className="w-16 tabular-nums flex-shrink-0 inline-flex items-center gap-1">
          {fmtDateShort(tx.date)}
          {tx.amortize && (
            <span className="inline-flex items-center gap-0.5 text-[9px] text-[#8a9bae]" title="Amortized 1/12 per month">
              <Divide className="h-2.5 w-2.5" />12
            </span>
          )}
        </span>
        <span className="flex-1 truncate px-1 inline-flex items-center gap-1">
          {display}
          {tx.moved_from_month && (
            <span
              className="inline-flex items-center gap-0.5 px-1 py-0 text-[9px] font-medium rounded border border-border/60 bg-muted/50 text-muted-foreground"
              title={`Originally dated ${tx.date}; moved into ${currentMonthYm} for this report`}
              data-testid="flex-tx-moved-badge"
            >
              <ArrowRightLeft className="h-2.5 w-2.5" />moved from {tx.moved_from_month}
            </span>
          )}
        </span>
      </button>

      {/* Section change popover (hover-only) */}
      {!isReadOnly && (
        <Popover open={popOpen} onOpenChange={setPopOpen}>
          <PopoverTrigger asChild>
            <button
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 flex-shrink-0 h-5 px-1.5 text-[9px] font-medium rounded border border-border/40 bg-white hover:bg-muted text-muted-foreground transition-opacity"
              data-testid="flex-tx-section-btn"
              title="Change classification"
            >
              {tx.section ? SECTION_META[tx.section].abbr : 'Set'}{tx.section === 'variable' && tx.variable_type ? `·${tx.variable_type[0]}` : ''}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-2" align="end" data-testid="flex-tx-section-popover">
            {moveMode ? (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Move to month</div>
                <Input
                  type="month"
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                  className="h-7 text-xs mb-2"
                  data-testid="flex-pop-move-input"
                />
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7 flex-1 text-[11px]" onClick={() => setMoveMode(false)}>Cancel</Button>
                  <Button
                    size="sm"
                    className="h-7 flex-1 text-[11px]"
                    disabled={!moveTarget || !/^\d{4}-\d{2}$/.test(moveTarget)}
                    onClick={() => { onMoveMonth(tx, moveTarget); setPopOpen(false); }}
                    data-testid="flex-pop-move-apply"
                  >
                    Move
                  </Button>
                </div>
                {tx.moved_to_month && (
                  <button
                    onClick={() => { onMoveMonth(tx, null); setPopOpen(false); }}
                    className="w-full mt-1.5 px-2 py-1 text-[10px] font-medium rounded border border-border/40 text-muted-foreground hover:bg-muted transition-colors"
                    data-testid="flex-pop-move-reset"
                  >
                    Reset to original month ({(tx.date || '').slice(0, 7)})
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Classify as</div>
                <div className="grid grid-cols-3 gap-1 mb-2">
                  {['cog', 'variable', 'general'].map(s => (
                    <button
                      key={s}
                      onClick={() => { onClassify(tx, s, s === 'variable' ? (tx.variable_type || 'random') : null); setPopOpen(false); }}
                      className={`px-1.5 py-1 text-[10px] font-medium rounded border transition-colors ${
                        tx.section === s
                          ? 'bg-[#1f2933] text-white border-[#1f2933]'
                          : 'bg-white border-border/60 text-[#5b6773] hover:bg-muted'
                      }`}
                      data-testid={`flex-pop-section-${s}`}
                    >
                      {SECTION_META[s].abbr}
                    </button>
                  ))}
                </div>
                {tx.section === 'variable' && (
                  <div className="mb-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Variable type</div>
                    <div className="grid grid-cols-2 gap-1">
                      {['seasonal', 'random'].map(vt => (
                        <button
                          key={vt}
                          onClick={() => onClassify(tx, 'variable', vt)}
                          className={`px-1.5 py-1 text-[10px] font-medium rounded border transition-colors ${
                            tx.variable_type === vt
                              ? 'bg-[#1f2933] text-white border-[#1f2933]'
                              : 'bg-white border-border/60 text-[#5b6773] hover:bg-muted'
                          }`}
                          data-testid={`flex-pop-vt-${vt}`}
                        >
                          {vt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {tx.section === 'general' && (
                  <div className="mb-2 pt-2 border-t border-border/40">
                    <button
                      onClick={() => { onToggleAmortize(tx); setPopOpen(false); }}
                      className={`w-full px-2 py-1 text-[10px] font-medium rounded border flex items-center justify-between transition-colors ${
                        tx.amortize
                          ? 'bg-[#1f2933] text-white border-[#1f2933]'
                          : 'bg-white border-border/60 text-[#5b6773] hover:bg-muted'
                      }`}
                      data-testid="flex-pop-amortize"
                      title="Spread 1/12 per month across calendar year"
                    >
                      <span className="inline-flex items-center gap-1"><Divide className="h-3 w-3" />Amortize 1/12 / mo</span>
                      <span>{tx.amortize ? 'ON' : 'OFF'}</span>
                    </button>
                  </div>
                )}
                <div className="pt-2 border-t border-border/40">
                  <button
                    onClick={() => setMoveMode(true)}
                    className="w-full px-2 py-1 text-[10px] font-medium rounded border border-border/60 text-[#5b6773] hover:bg-muted inline-flex items-center justify-between transition-colors"
                    data-testid="flex-pop-move-btn"
                    title="Move this transaction to a different month for the flexible report"
                  >
                    <span className="inline-flex items-center gap-1"><ArrowRightLeft className="h-3 w-3" />Move to month…</span>
                    {tx.moved_to_month && <span className="text-[9px] text-muted-foreground">in {tx.moved_to_month}</span>}
                  </button>
                </div>
                {(tx.section || tx.moved_to_month) && (
                  <button
                    onClick={() => { onClassify(tx, null); setPopOpen(false); }}
                    className="w-full mt-1 px-2 py-1 text-[10px] font-medium rounded border border-border/40 text-muted-foreground hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors"
                    data-testid="flex-pop-clear"
                  >
                    Clear all overrides
                  </button>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}

      <span className="tabular-nums flex-shrink-0 w-20 text-right">{fmt(tx.amount)}</span>
    </div>
  );
}

/* ─── Group transactions by category → subcategory ─── */
function groupTxns(transactions) {
  const catMap = new Map();
  for (const tx of transactions) {
    const cat = (tx.category || '(uncategorized)').trim() || '(uncategorized)';
    const sub = (tx.subcategory || '').trim();
    if (!catMap.has(cat)) catMap.set(cat, { name: cat, total: 0, subcategories: new Map(), direct: [] });
    const catNode = catMap.get(cat);
    catNode.total += tx.amount;
    if (sub) {
      if (!catNode.subcategories.has(sub)) catNode.subcategories.set(sub, { name: sub, total: 0, transactions: [] });
      const subNode = catNode.subcategories.get(sub);
      subNode.total += tx.amount;
      subNode.transactions.push(tx);
    } else {
      catNode.direct.push(tx);
    }
  }
  return Array.from(catMap.values())
    .map(c => ({
      ...c,
      total: Math.round(c.total * 100) / 100,
      subcategories: Array.from(c.subcategories.values())
        .map(s => ({ ...s, total: Math.round(s.total * 100) / 100 }))
        .sort((a, b) => b.total - a.total),
      direct: c.direct,
    }))
    .sort((a, b) => b.total - a.total);
}

/* ─── Subcategory ─── */
function SubcategoryRow({ sub, onOpen, onClassify, onToggleAmortize, onMoveMonth, isReadOnly, forceOpen, currentMonth: currentMonthYm }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  return (
    <div data-testid="flex-subcategory">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-1 px-1 -mx-1 rounded-sm hover:bg-[#f5f0eb]/40 transition-colors" data-testid="flex-subcategory-toggle">
        <span className="flex items-center gap-1.5 text-[12px] text-[#5b6773]">
          {isOpen ? <ChevronDown className="h-3 w-3 text-muted-foreground/60" /> : <ChevronRight className="h-3 w-3 text-muted-foreground/60" />}
          {sub.name}
        </span>
        <span className="text-[12px] tabular-nums text-[#5b6773]">{fmt(sub.total)}</span>
      </button>
      {isOpen && (
        <div className="ml-[18px]">
          {sub.transactions.map(tx => (
            <TxnRow key={tx.id} tx={tx} onOpen={onOpen} onClassify={onClassify} onToggleAmortize={onToggleAmortize} onMoveMonth={onMoveMonth} isReadOnly={isReadOnly} currentMonth={currentMonthYm} />
          ))}
          <div className="flex items-center justify-between py-0.5 px-1 mt-0.5 border-t border-border/30 text-[11px] italic text-[#8a9bae]">
            <span>Total {sub.name}</span>
            <span className="tabular-nums">{fmt(sub.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Category ─── */
function CategoryRow({ cat, onOpen, onClassify, onToggleAmortize, onMoveMonth, isReadOnly, forceOpen, currentMonth: currentMonthYm }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  return (
    <div data-testid="flex-category">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-1.5 px-1 -mx-1 rounded-sm hover:bg-[#f5f0eb]/60 transition-colors" data-testid="flex-category-toggle">
        <span className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
          {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-[13px] font-semibold text-[#1f2933]">{cat.name}</span>
        </span>
        <span className="text-[13px] font-semibold tabular-nums text-[#1f2933]">{fmt(cat.total)}</span>
      </button>
      {isOpen && (
        <div className="ml-5">
          {cat.subcategories.map(sub => (
            <SubcategoryRow key={sub.name} sub={sub} onOpen={onOpen} onClassify={onClassify} onToggleAmortize={onToggleAmortize} onMoveMonth={onMoveMonth} isReadOnly={isReadOnly} forceOpen={forceOpen} currentMonth={currentMonthYm} />
          ))}
          {cat.direct.map(tx => (
            <TxnRow key={tx.id} tx={tx} onOpen={onOpen} onClassify={onClassify} onToggleAmortize={onToggleAmortize} onMoveMonth={onMoveMonth} isReadOnly={isReadOnly} currentMonth={currentMonthYm} />
          ))}
          <div className="flex items-center justify-between py-1 px-1 mt-1 border-t border-border/50 text-[12px] font-medium text-[#5b6773]">
            <span>Total {cat.name}</span>
            <span className="tabular-nums">{fmt(cat.total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Top-level Section ─── */
function Section({ sectionKey, data, onOpen, onClassify, onToggleAmortize, onMoveMonth, isReadOnly, forceOpen, currentMonth: currentMonthYm }) {
  const [collapsed, setCollapsed] = useState(false);
  const meta = SECTION_META[sectionKey];
  const txCount = data.transactions.length;
  const cats = useMemo(() => groupTxns(data.transactions), [data.transactions]);
  if (txCount === 0 && (data.total || 0) === 0) return null;
  return (
    <div className="mb-4" data-testid={`flex-section-${sectionKey}`}>
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between py-1.5 mb-1 px-1 -mx-1 rounded-sm hover:bg-[#f5f0eb]/60 transition-colors"
        data-testid={`flex-section-toggle-${sectionKey}`}
      >
        <span className="flex items-center gap-1.5">
          {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-[13px] font-bold uppercase tracking-wide text-[#1f2933]">{meta.label}</span>
          <Badge variant="outline" className="text-[10px] font-normal h-5 px-1.5">{txCount}</Badge>
        </span>
        <span className="text-[13px] font-bold tabular-nums text-[#1f2933]" data-testid={`flex-section-total-${sectionKey}`}>{fmt(data.total)}</span>
      </button>
      {!collapsed && (
        <>
          <div className="ml-2">
            {cats.length > 0 ? (
              cats.map(cat => (
                <CategoryRow key={cat.name} cat={cat} onOpen={onOpen} onClassify={onClassify} onToggleAmortize={onToggleAmortize} onMoveMonth={onMoveMonth} isReadOnly={isReadOnly} forceOpen={forceOpen} currentMonth={currentMonthYm} />
              ))
            ) : (
              <div className="py-2 px-1 text-[11px] text-muted-foreground italic">No transactions</div>
            )}
          </div>
          <div className="border-t border-[#1f2933]/15 my-1.5" />
          <div className="flex items-center justify-between py-1.5">
            <span className="text-[13px] font-semibold text-[#5b6773]">Total {meta.label}</span>
            <span className="text-[13px] font-semibold tabular-nums text-[#5b6773]">{fmt(data.total)}</span>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Compare dialog ─── */
function ComparePanel({ open, onOpenChange }) {
  const [periodType, setPeriodType] = useState('month'); // month | quarter | year
  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth() + 1;
  const thisQuarter = Math.floor((thisMonth - 1) / 3) + 1;

  const [a, setA] = useState(`${thisYear}-${String(thisMonth).padStart(2, '0')}`);
  const [b, setB] = useState(addMonths(`${thisYear}-${String(thisMonth).padStart(2, '0')}`, -1));
  const [aData, setAData] = useState(null);
  const [bData, setBData] = useState(null);
  const [loading, setLoading] = useState(false);

  /* When periodType changes, reset pickers to sensible defaults */
  useEffect(() => {
    if (periodType === 'month') {
      const cur = `${thisYear}-${String(thisMonth).padStart(2, '0')}`;
      setA(cur); setB(addMonths(cur, -1));
    } else if (periodType === 'quarter') {
      setA(`${thisYear}-Q${thisQuarter}`);
      const prevQ = thisQuarter === 1 ? `${thisYear - 1}-Q4` : `${thisYear}-Q${thisQuarter - 1}`;
      setB(prevQ);
    } else {
      setA(String(thisYear));
      setB(String(thisYear - 1));
    }
    setAData(null); setBData(null);
  }, [periodType]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodToRange = (p) => {
    if (periodType === 'month') return { from: p, to: p, label: monthLabel(p) };
    if (periodType === 'quarter') {
      const [y, q] = p.split('-Q').map(Number);
      const startM = (q - 1) * 3 + 1;
      const endM = startM + 2;
      return {
        from: `${y}-${String(startM).padStart(2, '0')}`,
        to: `${y}-${String(endM).padStart(2, '0')}`,
        label: `Q${q} ${y}`,
      };
    }
    return { from: `${p}-01`, to: `${p}-12`, label: String(p) };
  };

  const runCompare = async () => {
    setLoading(true);
    try {
      const ra = periodToRange(a);
      const rb = periodToRange(b);
      const [da, db] = await Promise.all([
        getFlexibleAggregate(ra.from, ra.to),
        getFlexibleAggregate(rb.from, rb.to),
      ]);
      setAData({ ...da, label: ra.label });
      setBData({ ...db, label: rb.label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Compare failed');
    } finally { setLoading(false); }
  };

  const diff = (x, y) => (x || 0) - (y || 0);
  const pctDiff = (x, y) => {
    if (!y) return x ? '—' : '0%';
    return `${((x - y) / Math.abs(y) * 100).toFixed(1)}%`;
  };

  const periodOptions = useMemo(() => {
    if (periodType === 'month') {
      const out = [];
      let cur = `${thisYear}-${String(thisMonth).padStart(2, '0')}`;
      for (let i = 0; i < 36; i++) { out.push(cur); cur = addMonths(cur, -1); }
      return out.map(m => ({ value: m, label: monthLabel(m) }));
    }
    if (periodType === 'quarter') {
      const out = [];
      let y = thisYear, q = thisQuarter;
      for (let i = 0; i < 16; i++) {
        out.push({ value: `${y}-Q${q}`, label: `Q${q} ${y}` });
        q--; if (q === 0) { q = 4; y--; }
      }
      return out;
    }
    const out = [];
    for (let y = thisYear; y >= thisYear - 7; y--) out.push({ value: String(y), label: String(y) });
    return out;
  }, [periodType, thisYear, thisMonth, thisQuarter]);

  const rows = [
    { key: 'cog', label: 'Cost of Goods' },
    { key: 'variable', label: 'Variable' },
    { key: 'general', label: 'General Overhead' },
    { key: 'unclassified', label: 'Unclassified' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="flex-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="h-4 w-4" />Compare Periods</DialogTitle>
          <DialogDescription>Pick two periods to compare section totals side by side.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2 pb-3 border-b border-border/40">
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Compare by</span>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="flex-compare-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="quarter">Quarter</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Period A</span>
            <Select value={a} onValueChange={setA}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="flex-compare-a"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Period B</span>
            <Select value={b} onValueChange={setB}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="flex-compare-b"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={runCompare} disabled={loading} data-testid="flex-compare-run">
            {loading ? 'Loading…' : 'Compare'}
          </Button>
        </div>

        {aData && bData ? (
          <div className="mt-2" data-testid="flex-compare-results">
            <div className="grid grid-cols-4 gap-2 text-[11px] font-semibold text-[#1f2933] border-b border-border/50 pb-1.5 mb-1">
              <span>Section</span>
              <span className="text-right">{aData.label}</span>
              <span className="text-right">{bData.label}</span>
              <span className="text-right">Δ (A − B)</span>
            </div>
            {rows.map(r => {
              const av = aData[`total_${r.key}`] || 0;
              const bv = bData[`total_${r.key}`] || 0;
              const d = diff(av, bv);
              return (
                <div key={r.key} className="grid grid-cols-4 gap-2 py-1 text-[12px] border-b border-border/20" data-testid={`flex-compare-row-${r.key}`}>
                  <span className="font-medium text-[#1f2933]">{r.label}</span>
                  <span className="tabular-nums text-right">{fmt(av)}</span>
                  <span className="tabular-nums text-right">{fmt(bv)}</span>
                  <span className={`tabular-nums text-right ${d > 0 ? 'text-red-700' : d < 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                    {fmt(d)} <span className="text-[10px] text-muted-foreground">({pctDiff(av, bv)})</span>
                  </span>
                </div>
              );
            })}
            <div className="grid grid-cols-4 gap-2 py-1.5 text-[13px] font-bold text-[#1f2933] border-t-2 border-[#1f2933]/30 mt-1">
              <span>Grand Total</span>
              <span className="tabular-nums text-right">{fmt(aData.grand_total)}</span>
              <span className="tabular-nums text-right">{fmt(bData.grand_total)}</span>
              <span className={`tabular-nums text-right ${diff(aData.grand_total, bData.grand_total) > 0 ? 'text-red-700' : diff(aData.grand_total, bData.grand_total) < 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                {fmt(diff(aData.grand_total, bData.grand_total))}
              </span>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-xs text-muted-foreground">Pick two periods and click Compare.</div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main Page ─── */
export default function FlexibleReportsPage() {
  const [month, setMonth] = useState(currentMonth());
  const [versionId, setVersionId] = useState('working');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTxnsFull, setAllTxnsFull] = useState([]); // for TransactionEditDialog prev/next
  const [versions, setVersions] = useState([]);
  const [forceAll, setForceAll] = useState(undefined); // expand all / collapse all

  // Pending rule save prompt
  const [pendingRule, setPendingRule] = useState(null);

  // Version management dialogs
  const [saveVerOpen, setSaveVerOpen] = useState(false);
  const [verName, setVerName] = useState('');
  const [verDesc, setVerDesc] = useState('');
  const [verBusy, setVerBusy] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);

  // NL override dialog
  const [nlDialog, setNlDialog] = useState(null);
  const [nlInstruction, setNlInstruction] = useState('');
  const [nlBusy, setNlBusy] = useState(false);

  // AI classify
  const [aiBusy, setAiBusy] = useState(false);

  // Compare
  const [compareOpen, setCompareOpen] = useState(false);

  // Export
  const [exporting, setExporting] = useState(null);

  const isReadOnlyVersion = versionId !== 'working';

  /* Fetch report + full tx list (for edit dialog prev/next) */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [data, all] = await Promise.all([
        getFlexibleReport(month, versionId),
        getBankTransactions({}),
      ]);
      setReport(data);
      setAllTxnsFull(all);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [month, versionId]);

  useEffect(() => { refresh(); }, [refresh]);

  /* One-time fetches */
  useEffect(() => {
    (async () => {
      try {
        const [props, vers, cats] = await Promise.all([getProperties(), getFlexibleVersions(), getTransactionCategories()]);
        const filtered = (props || []).filter(p => !(p.name || '').toLowerCase().includes('test'));
        setProperties(filtered);
        setVersions(vers);
        setCategories(cats);
      } catch { /* ignore */ }
    })();
  }, []);

  /* Classify handler */
  const handleClassify = async (tx, section, variableType = null) => {
    if (isReadOnlyVersion) { toast.error('Switch to Working to edit'); return; }
    if (section === null) { await handleClear(tx); return; }
    let vt = variableType;
    if (section === 'variable' && !vt) vt = tx.variable_type || 'random';
    const hasBookkeeping = Boolean(tx.category);
    try {
      await upsertFlexibleAdjustment(tx.id, {
        section,
        variable_type: section === 'variable' ? vt : null,
        property_id: tx.property_id || null,
        amortize: section === 'general' ? !!tx.amortize : false,
        moved_to_month: tx.moved_to_month || null,
        note: tx.note || '',
      });
      await refresh();
      if (hasBookkeeping) {
        setPendingRule({ tx, section, variable_type: section === 'variable' ? vt : null });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const handleToggleAmortize = async (tx) => {
    if (isReadOnlyVersion) { toast.error('Switch to Working to edit'); return; }
    if (tx.section !== 'general') { toast.error('Amortization is only for General Overhead'); return; }
    try {
      await upsertFlexibleAdjustment(tx.id, {
        section: 'general',
        variable_type: null,
        property_id: tx.property_id || null,
        amortize: !tx.amortize,
        moved_to_month: tx.moved_to_month || null,
        note: tx.note || '',
      });
      await refresh();
      toast.success(tx.amortize ? 'Amortization removed' : 'Amortized 1/12 per month');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  const handleMoveMonth = async (tx, targetMonth) => {
    if (isReadOnlyVersion) { toast.error('Switch to Working to edit'); return; }
    try {
      await upsertFlexibleAdjustment(tx.id, {
        section: tx.section || null,
        variable_type: tx.variable_type,
        property_id: tx.property_id || null,
        amortize: tx.section === 'general' ? !!tx.amortize : false,
        moved_to_month: targetMonth || null,
        note: tx.note || '',
      });
      await refresh();
      if (targetMonth) {
        toast.success(`Moved to ${targetMonth} for this report`);
      } else {
        toast.success('Moved back to original month');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to move'); }
  };

  const handleClear = async (tx) => {
    if (isReadOnlyVersion) { toast.error('Switch to Working to edit'); return; }
    try {
      await clearFlexibleAdjustment(tx.id);
      await refresh();
    } catch { toast.error('Failed to clear'); }
  };

  /* Rule save decision */
  const handleRuleDecision = async (saveRule) => {
    if (!pendingRule) return;
    if (saveRule) {
      const { tx, section, variable_type } = pendingRule;
      try {
        await upsertFlexibleRule({
          category: tx.category || '',
          subcategory: tx.subcategory || '',
          section, variable_type,
        });
        toast.success(`Rule saved: ${tx.category}${tx.subcategory ? ' · ' + tx.subcategory : ''} → ${SECTION_META[section].abbr}`);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save rule'); }
    }
    setPendingRule(null);
  };

  /* AI classify */
  const handleAIClassify = async () => {
    if (isReadOnlyVersion) { toast.error('Switch to Working to edit'); return; }
    setAiBusy(true);
    try {
      const res = await aiClassifyFlexible(month);
      if (!res.count) { toast.info('Nothing to classify'); return; }
      for (const s of res.suggestions) {
        await upsertFlexibleAdjustment(s.id, {
          section: s.section,
          variable_type: s.variable_type,
          property_id: null,
          amortize: false,
          note: '',
        });
      }
      await refresh();
      toast.success(`AI classified ${res.suggestions.length} transaction${res.suggestions.length === 1 ? '' : 's'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI classify failed'); }
    finally { setAiBusy(false); }
  };

  /* NL override */
  const handleNLSubmit = async () => {
    if (!nlDialog || !nlInstruction.trim()) return;
    setNlBusy(true);
    try {
      const parsed = await aiParseFlexibleInstruction(nlDialog.tx.id, nlInstruction);
      if (!parsed.section) { toast.error(parsed.error || 'Could not interpret'); return; }
      await upsertFlexibleAdjustment(nlDialog.tx.id, {
        section: parsed.section,
        variable_type: parsed.variable_type,
        property_id: nlDialog.tx.property_id || null,
        amortize: false,
        note: nlInstruction,
      });
      toast.success(`Classified as ${SECTION_META[parsed.section].abbr}${parsed.variable_type ? ` (${parsed.variable_type})` : ''}`);
      setNlDialog(null);
      setNlInstruction('');
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI failed'); }
    finally { setNlBusy(false); }
  };

  /* Version save */
  const handleSaveVersion = async () => {
    const name = verName.trim();
    if (!name) return;
    setVerBusy(true);
    try {
      await createFlexibleVersion({ name, description: verDesc, month });
      toast.success(`Version "${name}" saved`);
      setVerName(''); setVerDesc(''); setSaveVerOpen(false);
      const vers = await getFlexibleVersions();
      setVersions(vers);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save version'); }
    finally { setVerBusy(false); }
  };

  const handleDeleteVersion = async (v) => {
    if (!window.confirm(`Delete version "${v.name}"? This cannot be undone.`)) return;
    try {
      await deleteFlexibleVersion(v.id);
      toast.success('Version deleted');
      if (versionId === v.id) setVersionId('working');
      const vers = await getFlexibleVersions();
      setVersions(vers);
    } catch { toast.error('Failed to delete'); }
  };

  /* Open edit dialog */
  const handleOpenEdit = (tx) => {
    const idx = allTxnsFull.findIndex(t => t.id === tx.id);
    if (idx >= 0) { setEditIndex(idx); setEditOpen(true); }
    else { toast.error('Transaction not found'); }
  };

  /* Export handler */
  const handleExport = async (format) => {
    setExporting(format);
    try {
      await downloadFlexibleExport(format, month, versionId);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally { setExporting(null); }
  };

  const editTarget = editIndex >= 0 && editIndex < allTxnsFull.length ? allTxnsFull[editIndex] : null;
  const selectedVersion = versions.find(v => v.id === versionId);

  return (
    <div className="space-y-4" data-testid="flexible-reports-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#1f2933]" data-testid="flex-page-title">Flexible Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Month-at-a-time expense classification. Does not modify Transactions or standard Reports.</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => setCompareOpen(true)} data-testid="flex-compare-btn">
            <GitCompare className="h-3.5 w-3.5 mr-1.5" />Compare
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={exporting === 'csv'} data-testid="flex-export-csv-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" />{exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'} data-testid="flex-export-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" />{exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="flex-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1.5" />Print
          </Button>
          <Select value={versionId} onValueChange={setVersionId}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="flex-version-select">
              <History className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="working">Working (live)</SelectItem>
              {versions.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setSaveVerOpen(true)} disabled={isReadOnlyVersion} data-testid="flex-save-version-btn">
            <Save className="h-3.5 w-3.5" />Save Version
          </Button>
          {isReadOnlyVersion && selectedVersion && (
            <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:bg-red-50" onClick={() => handleDeleteVersion(selectedVersion)} data-testid="flex-delete-version-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          )}
        </div>
      </div>

      {/* Month controls */}
      <div className="flex items-end gap-2 flex-wrap print:hidden">
        <div className="inline-flex items-center gap-1 bg-white rounded-md border border-border/60 px-1" data-testid="flex-month-picker">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(addMonths(month, -1))} data-testid="flex-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 border-0 w-[140px] text-xs tabular-nums" data-testid="flex-month-input" />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(addMonths(month, 1))} data-testid="flex-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setForceAll(true)} data-testid="flex-expand-all-btn">Expand all</Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setForceAll(false)} data-testid="flex-collapse-all-btn">Collapse all</Button>
        {forceAll !== undefined && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setForceAll(undefined)} data-testid="flex-reset-btn">Reset</Button>
        )}
      </div>

      {isReadOnlyVersion && (
        <div className="flex items-center gap-2 px-3 py-2 rounded border border-border/60 bg-muted/40 text-[12px] text-[#5b6773]" data-testid="flex-readonly-banner">
          <History className="h-3.5 w-3.5" />
          Viewing saved version <strong>{selectedVersion?.name}</strong>. Switch to <em>Working</em> to edit.
        </div>
      )}

      {/* Unclassified strip */}
      {report && report.unclassified.transactions.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 rounded border border-border/60 bg-muted/40 text-[12px] print:hidden" data-testid="flex-unclassified-strip">
          <div className="flex items-center gap-2 text-[#5b6773]">
            <AlertCircle className="h-4 w-4" />
            <strong>{report.unclassified.transactions.length}</strong> unclassified expense{report.unclassified.transactions.length === 1 ? '' : 's'} totaling <strong>{fmt(report.total_unclassified)}</strong>
          </div>
          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={handleAIClassify} disabled={aiBusy || isReadOnlyVersion} data-testid="flex-ai-classify-btn">
            <Wand2 className="h-3 w-3" />
            {aiBusy ? 'Classifying…' : 'AI Classify All'}
          </Button>
        </div>
      )}

      {/* Report card (matches ReportsPage aesthetic) */}
      <div className="bg-white rounded-xl border border-border/60 shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_30px_rgba(16,24,40,0.06)] overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="flex-report-card">
        <div className="text-center py-6 border-b border-border/40 print:py-4">
          <h2 className="text-lg font-heading font-bold text-[#1f2933]" data-testid="flex-report-title">Flexible P&L</h2>
          <p className="text-xs text-muted-foreground mt-1">{monthLabel(month)}</p>
        </div>

        {loading && !report ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
        ) : report ? (
          <div className="px-6 sm:px-10 py-4 print:px-4">
            <div className="flex justify-end pb-2 border-b border-[#1f2933]/10 mb-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Total</span>
            </div>

            <Section sectionKey="cog"      data={report.cog}          onOpen={handleOpenEdit} onClassify={handleClassify} onToggleAmortize={handleToggleAmortize} onMoveMonth={handleMoveMonth} isReadOnly={isReadOnlyVersion} forceOpen={forceAll} currentMonth={month} />
            <Section sectionKey="variable" data={report.variable}     onOpen={handleOpenEdit} onClassify={handleClassify} onToggleAmortize={handleToggleAmortize} onMoveMonth={handleMoveMonth} isReadOnly={isReadOnlyVersion} forceOpen={forceAll} currentMonth={month} />
            <Section sectionKey="general"  data={report.general}      onOpen={handleOpenEdit} onClassify={handleClassify} onToggleAmortize={handleToggleAmortize} onMoveMonth={handleMoveMonth} isReadOnly={isReadOnlyVersion} forceOpen={forceAll} currentMonth={month} />
            {report.unclassified.transactions.length > 0 && (
              <Section sectionKey="unclassified" data={report.unclassified} onOpen={handleOpenEdit} onClassify={handleClassify} onToggleAmortize={handleToggleAmortize} onMoveMonth={handleMoveMonth} isReadOnly={isReadOnlyVersion} forceOpen={forceAll} currentMonth={month} />
            )}

            <div className="flex items-center justify-between py-1.5 border-t-2 border-[#1f2933]/25 pt-3 mt-1" data-testid="flex-grand-total">
              <span className="text-[13px] font-bold text-[#1f2933]">Grand Total</span>
              <span className="text-[13px] font-bold tabular-nums text-[#1f2933]">{fmt(report.grand_total)}</span>
            </div>
          </div>
        ) : null}

        <div className="px-6 sm:px-10 py-3 border-t border-border/30 text-[10px] text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" />
          Hover a row to change section, amortize (General), or move to a different month · Click a row to see/edit full transaction details.
        </div>
      </div>

      {/* Edit dialog — uses same component as main Reports page */}
      <TransactionEditDialog
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditIndex(-1); }}
        transaction={editTarget}
        categories={categories}
        onSaved={() => refresh()}
        onPrev={() => setEditIndex(i => Math.max(0, i - 1))}
        onNext={() => setEditIndex(i => Math.min(allTxnsFull.length - 1, i + 1))}
        hasPrev={editIndex > 0}
        hasNext={editIndex >= 0 && editIndex < allTxnsFull.length - 1}
        position={editIndex >= 0 ? { current: editIndex + 1, total: allTxnsFull.length } : null}
      />

      {/* Compare dialog */}
      <ComparePanel open={compareOpen} onOpenChange={setCompareOpen} />

      {/* Save-as-rule prompt */}
      <Dialog open={!!pendingRule} onOpenChange={v => !v && setPendingRule(null)}>
        <DialogContent className="max-w-md" data-testid="flex-rule-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Save className="h-4 w-4" />Save as rule?</DialogTitle>
            <DialogDescription>This transaction has a bookkeeping category. Save this classification as a reusable rule?</DialogDescription>
          </DialogHeader>
          {pendingRule && (
            <div className="space-y-2 py-2">
              <div className="text-xs text-muted-foreground">Rule:</div>
              <div className="px-3 py-2 rounded border border-border/60 bg-muted/40 text-sm">
                <span className="font-semibold">{pendingRule.tx.category}</span>
                {pendingRule.tx.subcategory && <span className="text-muted-foreground"> · {pendingRule.tx.subcategory}</span>}
                <span className="mx-2">→</span>
                <Badge variant="outline">
                  {SECTION_META[pendingRule.section].label}
                  {pendingRule.variable_type ? ` (${pendingRule.variable_type})` : ''}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground">
                All future and existing transactions with this category will use this classification (unless overridden per-tx).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handleRuleDecision(false)} data-testid="flex-rule-skip-btn">Just this one</Button>
            <Button onClick={() => handleRuleDecision(true)} data-testid="flex-rule-save-btn">Save as rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NL override dialog */}
      <Dialog open={!!nlDialog} onOpenChange={v => { if (!v) { setNlDialog(null); setNlInstruction(''); } }}>
        <DialogContent className="max-w-md" data-testid="flex-nl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-4 w-4" />Tell AI how to classify</DialogTitle>
            <DialogDescription>Describe how this transaction should be classified. The AI will also learn from your prior decisions.</DialogDescription>
          </DialogHeader>
          {nlDialog && (
            <div className="space-y-2 py-1">
              <div className="text-[11px] px-2 py-1.5 rounded bg-muted/40 border border-border/40">
                <div className="font-medium truncate">{nlDialog.tx.title || nlDialog.tx.description}</div>
                <div className="text-muted-foreground tabular-nums">{fmtDateShort(nlDialog.tx.date)} · {fmt(nlDialog.tx.amount)}</div>
              </div>
              <Textarea value={nlInstruction} onChange={e => setNlInstruction(e.target.value)} placeholder='e.g., "This is a seasonal cleaning, put in Variable"' className="text-xs" rows={3} autoFocus data-testid="flex-nl-instruction-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNlDialog(null); setNlInstruction(''); }}>Cancel</Button>
            <Button onClick={handleNLSubmit} disabled={nlBusy || !nlInstruction.trim()} data-testid="flex-nl-submit-btn">{nlBusy ? 'Parsing…' : 'Apply'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save version dialog */}
      <Dialog open={saveVerOpen} onOpenChange={setSaveVerOpen}>
        <DialogContent className="max-w-sm" data-testid="flex-save-version-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Save className="h-4 w-4" />Save Current State as Version</DialogTitle>
            <DialogDescription>Snapshots all current adjustments + rules. The working state continues independently.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Input value={verName} onChange={e => setVerName(e.target.value)} placeholder="e.g., January 2026 Final" autoFocus data-testid="flex-version-name-input" />
            <Textarea value={verDesc} onChange={e => setVerDesc(e.target.value)} placeholder="Optional description" rows={2} className="text-xs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveVerOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveVersion} disabled={!verName.trim() || verBusy} data-testid="flex-version-save-confirm-btn">{verBusy ? 'Saving…' : 'Save Version'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
